/**
 * 318 (FED-55): the Gallery's map panel — beside the grid on a wide screen,
 * above it on a phone. It says how many photos are on the map and how many
 * have no location (they stay in the grid), and lazy-loads the map itself.
 *
 * The counts come from photo_room_map(); nothing here counts photos itself.
 */
import { Component, lazy, Suspense, type ReactNode } from 'react'
import { isCapped, withoutLocation, type GalleryMapData, type MapPick } from '../lib/galleryMap'
import type { GalleryMapCopy } from '../lib/hubCopy'

const GalleryMap = lazy(() => import('./GalleryMap'))

/** A chunk that fails to load, or a map that throws, leaves a sentence — not a blank page. */
class MapBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { broken: boolean }> {
  state = { broken: false }
  static getDerivedStateFromError() {
    return { broken: true }
  }
  render() {
    return this.state.broken ? this.props.fallback : this.props.children
  }
}

export function GalleryMapPanel({
  data,
  error,
  picked,
  onPick,
  t,
  mapKey,
}: {
  data: GalleryMapData | null
  error: string | null
  picked: MapPick | null
  onPick: (pick: MapPick) => void
  t: GalleryMapCopy
  mapKey: string | null
}) {
  const without = data ? withoutLocation(data) : 0
  return (
    <aside id="gallery-map" className="gmap" aria-label={t.region}>
      <p className="gmap__count" role="status">
        {error ? t.failed : !data ? t.loading : data.located === 0 ? t.none(without) : t.counts(data.located, without)}
        {data && isCapped(data) ? <span className="gmap__capped"> {t.capped(data.points.length, data.located)}</span> : null}
      </p>
      <div className="gmap__frame">
        {data && !error && (
          <MapBoundary fallback={<p className="gmap__wait">{t.failed}</p>}>
            <Suspense fallback={<p className="gmap__wait">{t.loading}</p>}>
              <GalleryMap points={data.points} picked={picked} onPick={onPick} t={t} mapKey={mapKey} />
            </Suspense>
          </MapBoundary>
        )}
      </div>
      {!mapKey && <p className="gmap__note">{t.outline}</p>}
    </aside>
  )
}
